import { useCallback, useState } from "react";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import { DialogModal, Menu } from "@pushpress/shared-components";
import Dashboard from "../../../layouts/Dashboard";
import { useToggle } from "../../../hooks/state";
import { defaultToastOptions } from "../../../utils/misc";
import ClassTypesHeader from "./ClassTypesHeader";
import ClassTypesTable from "./ClassTypesTable";
import ClassTypeDrawer from "./ClassTypeDrawer";
import {
  ClassTypeRowFragment,
  useDeleteCalendarClassTypeMutation,
  useGetCalendarClassTypesQuery,
} from "./ClassTypes-generated.hooks";

function ClassTypesList() {
  return (
    <Dashboard>
      <Helmet>
        <title>Class Types</title>
      </Helmet>
      <ClassTypesListContent />
    </Dashboard>
  );
}

export function ClassTypesListContent() {
  const [drawerVisible, drawerActions] = useToggle(false);
  const [deleteVisible, deleteActions] = useToggle(false);
  const [selectedRow, setSelectedRow] = useState<ClassTypeRowFragment>();

  const classTypesQuery = useGetCalendarClassTypesQuery({
    variables: {
      active: true,
    },
  });

  const [deleteMutation] = useDeleteCalendarClassTypeMutation();

  const handleAddClick = () => {
    setSelectedRow(undefined);
    drawerActions.on();
  };

  const handleDeleteClick = async () => {
    await deleteMutation({
      variables: {
        uuid: selectedRow!.uuid,
      },
    });
    await classTypesQuery.refetch();
    toast.success("Class type deleted", defaultToastOptions);
  };

  const handleSuccess = () => {
    drawerActions.off();
    classTypesQuery.refetch();
  };

  const renderRowMenu = useCallback(
    (row: ClassTypeRowFragment, closeMenu: VoidFunction) => {
      const handleEditClick = () => {
        drawerActions.on();
      };

      if (!row?.clientUuid) {
        return null;
      }
      setSelectedRow(row);
      return (
        <Menu>
          <Menu.Item
            label="Edit"
            action={() => {
              handleEditClick();
              closeMenu();
            }}
          />
          <Menu.Item
            label="Delete"
            action={() => {
              deleteActions.on();
              closeMenu();
            }}
          />
        </Menu>
      );
    },
    [deleteActions, drawerActions],
  );

  const data = classTypesQuery?.data?.getCalendarClassTypes || [];

  return (
    <>
      <div>
        <ClassTypesHeader onAddClassTypeClick={handleAddClick} />
        <div className="mt-4">
          <ClassTypesTable
            data={data}
            loading={classTypesQuery.loading}
            renderRowMenu={renderRowMenu}
          />
        </div>
      </div>
      <ClassTypeDrawer
        uuid={selectedRow?.uuid}
        visible={drawerVisible}
        onClose={drawerActions.off}
        onSuccess={handleSuccess}
      />
      <DialogModal
        width="342px"
        title="Are you sure you want to delete this class type?"
        visible={deleteVisible}
        primaryBtnTitle="Delete class type"
        secondaryBtnTitle="Go back"
        primaryHandler={handleDeleteClick}
        closeModal={deleteActions.off}
        secondaryHandler={deleteActions.off}
      />
    </>
  );
}

export default ClassTypesList;
